import { useState, useEffect, useRef } from 'react';

interface TimeAgoResult {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isFinished: boolean;
}

export const useTimeAgo = (targetDateMs: number, onFinish?: () => void): TimeAgoResult => {
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, targetDateMs - Date.now()));
  const [isFinished, setIsFinished] = useState(timeLeft <= 0);
  const prevTargetRef = useRef(targetDateMs);

  // targetDateMs가 변경되면 상태 재평가 (0 → 실제 시각 등)
  useEffect(() => {
    if (prevTargetRef.current === targetDateMs) return;
    prevTargetRef.current = targetDateMs;

    const remaining = Math.max(0, targetDateMs - Date.now());
    setTimeLeft(remaining);
    if (remaining > 0) {
      setIsFinished(false);
    } else {
      setIsFinished(true);
      if (onFinish) onFinish();
    }
  }, [targetDateMs, onFinish]);

  // 마운트 시 이미 시간이 지났으면 즉시 onFinish 호출
  useEffect(() => {
    if (isFinished && onFinish) {
      onFinish();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isFinished) return;

    const timer = setInterval(() => {
      const remaining = Math.max(0, targetDateMs - Date.now());
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(timer);
        setIsFinished(true);
        if (onFinish) {
          onFinish();
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDateMs, isFinished, onFinish]);

  const totalSeconds = Math.floor(timeLeft / 1000);
  const days = Math.floor(totalSeconds / (3600 * 24));
  const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds, isFinished };
};
